// Join codes, as typed by a human at a first tee. Pure.
//
// The server generates these in Crockford base32, which leaves out I, L, O and U precisely
// so a code cannot be misread off a sign or misheard down a phone. The other half of that
// bargain is honouring the substitutions on the way back in: somebody who reads O for zero
// and types it should get into the event, not an error message, while a hundred and twenty
// people are trying to tee off.
//
// Also forgives the things people do to a code without thinking: lower case, a hyphen in
// the middle because it looks like a licence plate, and a trailing space from a paste.

/** Crockford base32 — the alphabet the server generates from. */
const ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

export const JOIN_CODE_LENGTH = 6;

/**
 * Normalise what somebody typed into what the server stores.
 *
 * Substitutions are Crockford's own: I and L read as 1, O reads as 0, U reads as V. They
 * are not guesses — the alphabet excludes those letters so that the mapping is unambiguous.
 */
export function normalizeJoinCode(input: string): string {
  return (input ?? '')
    .toUpperCase()
    .replace(/[\s\-–—_.]/g, '')
    .replace(/[IL]/g, '1')
    .replace(/O/g, '0')
    .replace(/U/g, 'V');
}

/** True when a normalised code could be real — right length, right alphabet. */
export function isValidJoinCode(input: string): boolean {
  const code = normalizeJoinCode(input);
  if (code.length !== JOIN_CODE_LENGTH) return false;
  return [...code].every((ch) => ALPHABET.includes(ch));
}

/**
 * Why a code will not do, in words worth showing someone.
 *
 * Returns null when it is fine. Deliberately distinguishes "not finished typing" from
 * "that cannot be a code", because the first is not an error and should not be shouted at
 * somebody halfway through six characters.
 */
export function joinCodeProblem(input: string): string | null {
  const code = normalizeJoinCode(input);
  if (code.length === 0) return null;
  if (code.length < JOIN_CODE_LENGTH) return null;
  if (code.length > JOIN_CODE_LENGTH) return `A code is ${JOIN_CODE_LENGTH} characters`;
  const bad = [...code].find((ch) => !ALPHABET.includes(ch));
  if (bad) return `“${bad}” is not part of a code`;
  return null;
}

/** Group a code for display so it can be read aloud: "21P Z8J". */
export function formatJoinCode(code: string): string {
  const c = normalizeJoinCode(code);
  if (c.length !== JOIN_CODE_LENGTH) return c;
  return `${c.slice(0, 3)} ${c.slice(3)}`;
}
